// ============================================================
// useRollActions - action-sheet rolls: characteristic tests,
// skill tests, plain-target derived tests, and free-form dice
// expressions. Each roll produces a structured narrative entry
// routed to both the local tab log and the shared session log
// via addRollNarrative (which, unlike addNarrative, does not
// enrich with the active target's name - attribution is the
// name resolved here). Instantiated once per side: the attacker
// sheet passes the loaded character; the defender sheet passes
// the current tab's sourceCharacter + target (for derived
// tests) and the target/tab name for attribution.
// ============================================================
import { useCallback } from 'react';
import { useStableHandlers } from './useStableCallback';
import {
  rollCharacteristicTest,
  rollSkillTest,
  rollPlainTargetTest,
  buildTestNarrative,
  buildDiceRollNarrative,
  SKILL_TEST_DEFS,
} from '../engine/characteristicTests';
import { rollOpposedTest, buildOpposedNarrative, type OpposedResult } from '../engine/opposedTests';
import { opposedSide, isDeadFooting } from '../engine/opposedFooting';
import type { TestResult, SkillTestDef, PlainTargetTestId } from '../engine/characteristicTests';
import { DEFAULT_UNNATURAL_DOS_MODE, type UnnaturalDosMode } from '../engine/unnaturalDos';
import { parseDiceExpression, rollDiceExpression } from '../engine/dice';
import type { Character, CharacteristicId } from '../engine/character/types';
import type { DiceResult, NarrativeEntry, Target } from '../types';

/** A labelled extra modifier (e.g. a condition penalty) the sheet folds into the test. */
type ExtraMod = { label: string; value: number };

interface UseRollActionsParams {
  character: Character | null;
  /** Attribution override for narrative entries (e.g. the target/tab name on the defender side). */
  characterName?: string | null;
  /** Enables rollPlainTarget (derived Dodge/Parry/Tgh tests for targets without a character). */
  target?: Target | null;
  /** Last-resort attribution name. Defaults to 'Attacker'. */
  fallbackName?: string;
  /** Active Unnatural DoS house rule; defaults to 'off'. */
  unnaturalDosMode?: UnnaturalDosMode;
  /** Transient per-characteristic ×N overrides (sheet badge edits) - wins over stored multipliers. */
  unnaturalOverrides?: Partial<Record<CharacteristicId, number>>;
  addRollNarrative: (entry: NarrativeEntry) => void;
  /**
   * Who a RIGHT-clicked test opposes (v8.220.0): the current defender
   * on the attacker sheet, the acting character on a defender sheet.
   * Absent = right-click does nothing on this instance.
   */
  opponent?: { name: string; character?: Character | null; target?: Target | null } | null;
}

export interface UseRollActionsReturn {
  rollCharacteristic: (
    charId: CharacteristicId,
    difficultyMod: number,
    customMod: number,
    extraMod?: { label: string; value: number },
  ) => TestResult | null;
  rollSkill: (
    skillId: SkillTestDef['id'],
    difficultyMod: number,
    customMod: number,
    extraMod?: { label: string; value: number },
  ) => TestResult | null;
  rollPlainTarget: (
    testId: PlainTargetTestId,
    difficultyMod: number,
    customMod: number,
    extraMod?: { label: string; value: number },
  ) => TestResult | null;
  rollDiceExpr: (expr: string) => DiceResult | null;
  /**
   * Opposed roll vs `opponent` - both sides at once, one combined log
   * entry. Returns false when no opposed footing exists (no opponent, a
   * dead opponent, or a characteristic a statblock cannot oppose) - the
   * caller shows the reason it already printed to the log.
   */
  rollOpposedCharacteristic: (charId: CharacteristicId) => boolean;
  rollOpposedSkill: (skillId: SkillTestDef['id']) => boolean;
}

type Opponent = NonNullable<UseRollActionsParams['opponent']>;
type Notice = (text: string) => void;

/** The parameters with their defaults, and the two attribution names, resolved once per render. */
interface RollContext {
  character: Character | null;
  target: Target | null;
  mode: UnnaturalDosMode;
  unnaturalOverrides?: Partial<Record<CharacteristicId, number>>;
  opponent: Opponent | null;
  addRollNarrative: (entry: NarrativeEntry) => void;
  characterAttribution: string;
  targetAttribution: string;
}

function rollContext({
  character,
  characterName,
  target = null,
  fallbackName = 'Attacker',
  unnaturalDosMode = DEFAULT_UNNATURAL_DOS_MODE,
  unnaturalOverrides,
  addRollNarrative,
  opponent = null,
}: UseRollActionsParams): RollContext {
  return {
    character,
    target,
    mode: unnaturalDosMode,
    unnaturalOverrides,
    opponent,
    addRollNarrative,
    characterAttribution: characterName || character?.name || fallbackName,
    targetAttribution: characterName || target?.name || fallbackName,
  };
}

/** Why a characteristic cannot be opposed, or null when both sides have footing. */
function characteristicRefusal(opponent: Opponent, sidesFound: boolean, charId: CharacteristicId): string | null {
  if (isDeadFooting(opponent)) return `${opponent.name} is dead - nothing left to oppose.`;
  return sidesFound ? null : `No opposed ${charId.toUpperCase()} axis against ${opponent.name}.`;
}

/**
 * Why a skill cannot be opposed, or null. Skills oppose skill-for-skill,
 * which needs a real sheet on both sides - a statblock has no skill list.
 */
function skillRefusal(opponent: Opponent, character: Character | null): string | null {
  if (isDeadFooting(opponent)) return `${opponent.name} is dead - nothing left to oppose.`;
  return character && opponent.character ? null : `Opposed skill tests need character sheets on both sides.`;
}

/** Roll an opposed skill test; rollSkillTest refuses untrained advanced skills - either side - and that is said. */
function tryOpposedSkill(
  sides: [Character, Character],
  skillId: SkillTestDef['id'],
  unnaturalDosMode: UnnaturalDosMode,
  publishNotice: Notice,
): OpposedResult | null {
  try {
    return rollOpposedTest(
      { kind: 'skill', character: sides[0], skillId },
      { kind: 'skill', character: sides[1], skillId },
      { unnaturalDosMode },
    );
  } catch (err) {
    publishNotice(err instanceof Error ? err.message : String(err));
    return null;
  }
}

/** The character sheet's own tests: a characteristic, or a skill (Unnatural read off its governing one). */
function useCharacterRolls(c: RollContext, publishTest: (result: TestResult, name: string) => void) {
  const { character, characterAttribution, mode, unnaturalOverrides } = c;
  const rollCharacteristic = useCallback(
    (charId: CharacteristicId, difficultyMod: number, customMod: number, extraMod?: ExtraMod): TestResult | null => {
      if (!character) return null;
      const opts = { mode, multiplierOverride: unnaturalOverrides?.[charId], extraMod };
      const result = rollCharacteristicTest(character, charId, difficultyMod, customMod, opts);
      publishTest(result, characterAttribution);
      return result;
    },
    [character, characterAttribution, publishTest, mode, unnaturalOverrides],
  );
  const rollSkill = useCallback(
    (skillId: SkillTestDef['id'], difficultyMod: number, customMod: number, extraMod?: ExtraMod): TestResult | null => {
      if (!character) return null;
      const governing = SKILL_TEST_DEFS.find((d) => d.id === skillId)!.governing;
      const opts = { mode, multiplierOverride: unnaturalOverrides?.[governing], extraMod };
      const result = rollSkillTest(character, skillId, difficultyMod, customMod, opts);
      publishTest(result, characterAttribution);
      return result;
    },
    [character, characterAttribution, publishTest, mode, unnaturalOverrides],
  );
  return { rollCharacteristic, rollSkill };
}

/** A statblock's derived tests, and a free-form dice expression. */
function usePlainRolls(c: RollContext, publishTest: (result: TestResult, name: string) => void) {
  const { target, targetAttribution, mode, addRollNarrative } = c;
  const rollPlainTarget = useCallback(
    (testId: PlainTargetTestId, difficultyMod: number, customMod: number, extraMod?: ExtraMod): TestResult | null => {
      if (!target) return null;
      const result = rollPlainTargetTest(target, testId, difficultyMod, customMod, { mode, extraMod });
      publishTest(result, targetAttribution);
      return result;
    },
    [target, targetAttribution, publishTest, mode],
  );
  const rollDiceExpr = useCallback(
    (expr: string): DiceResult | null => {
      if (parseDiceExpression(expr).length === 0) return null;
      const result = rollDiceExpression(expr);
      const structured = buildDiceRollNarrative(expr, result);
      addRollNarrative({ type: 'dice-roll', text: structured.summary, structured, timestamp: Date.now() });
      return result;
    },
    [addRollNarrative],
  );
  return { rollPlainTarget, rollDiceExpr };
}

/** One combined entry for both sides of an opposed roll. */
function useOpposedPublisher(c: RollContext) {
  const { characterAttribution, addRollNarrative } = c;
  return useCallback(
    (result: OpposedResult, opponentName: string, label: string) => {
      const structured = buildOpposedNarrative(result, characterAttribution, opponentName, label);
      addRollNarrative({ type: 'test-result', text: structured.summary, structured, timestamp: Date.now() });
    },
    [characterAttribution, addRollNarrative],
  );
}

type OpposedPublish = ReturnType<typeof useOpposedPublisher>;

/** Right-click on a characteristic: this instance's footing against the opponent's. */
function useOpposedCharacteristic(c: RollContext, publishOpposed: OpposedPublish, publishNotice: Notice) {
  const { character, target, mode, opponent } = c;
  return useCallback(
    (charId: CharacteristicId): boolean => {
      if (!opponent) return false;
      // This instance's own footing - its character, else its plain axes - against theirs.
      const mine = opposedSide({ character, target }, charId);
      const theirs = opposedSide(opponent, charId);
      const refusal = characteristicRefusal(opponent, !!(mine && theirs), charId);
      if (refusal) {
        publishNotice(refusal);
        return false;
      }
      const result = rollOpposedTest(mine!, theirs!, { unnaturalDosMode: mode });
      publishOpposed(result, opponent.name, `Opposed ${charId.toUpperCase()}`);
      return true;
    },
    [opponent, character, target, publishOpposed, publishNotice, mode],
  );
}

/** Right-click on a skill: skill against skill, sheet against sheet. */
function useOpposedSkill(c: RollContext, publishOpposed: OpposedPublish, publishNotice: Notice) {
  const { character, mode, opponent } = c;
  return useCallback(
    (skillId: SkillTestDef['id']): boolean => {
      if (!opponent) return false;
      const refusal = skillRefusal(opponent, character);
      if (refusal) {
        publishNotice(refusal);
        return false;
      }
      const def = SKILL_TEST_DEFS.find((d) => d.id === skillId)!;
      const result = tryOpposedSkill([character!, opponent.character!], skillId, mode, publishNotice);
      if (!result) return false;
      publishOpposed(result, opponent.name, `Opposed ${def.label}`);
      return true;
    },
    [opponent, character, publishOpposed, publishNotice, mode],
  );
}

/** A test result, and a plain notice, into the roll log. */
function usePublishers(addRollNarrative: RollContext['addRollNarrative']) {
  const publishTest = useCallback(
    (result: TestResult, name: string) => {
      const structured = buildTestNarrative(result, name);
      addRollNarrative({ type: 'test-result', text: structured.summary, structured, timestamp: Date.now() });
    },
    [addRollNarrative],
  );
  const publishNotice = useCallback(
    (text: string) => addRollNarrative({ type: 'info', text, timestamp: Date.now() }),
    [addRollNarrative],
  );
  return { publishTest, publishNotice };
}

export default function useRollActions(params: UseRollActionsParams): UseRollActionsReturn {
  const c = rollContext(params);
  const { publishTest, publishNotice } = usePublishers(c.addRollNarrative);
  const { rollCharacteristic, rollSkill } = useCharacterRolls(c, publishTest);
  const { rollPlainTarget, rollDiceExpr } = usePlainRolls(c, publishTest);
  const publishOpposed = useOpposedPublisher(c);
  const rollOpposedCharacteristic = useOpposedCharacteristic(c, publishOpposed, publishNotice);
  const rollOpposedSkill = useOpposedSkill(c, publishOpposed, publishNotice);
  // The same object of the same functions for the component's life, each rolling with the latest sheet (plan
  // shell-contexts, A2): they are only called from a click, and every context holding them changed with each render.
  return useStableHandlers({
    rollCharacteristic,
    rollSkill,
    rollPlainTarget,
    rollDiceExpr,
    rollOpposedCharacteristic,
    rollOpposedSkill,
  });
}
